import React, { useRef, useState } from 'react';
import { f0, fc, calcPayoff, MOS } from '../utils';

export default function CheckIn({ state, updateState, showToast, triggerConfetti }) {
  const {
    cards = [],
    extra = 0,
    method = 'snowball',
    lump = 0,
    savContrib = 0,
    savGoal = 1000,
    savLog = [],
    checkins = [],
    extraGoals = [],
    savGoalName = 'Savings',
  } = state;
  const savBalRef = useRef();
  const ciRefs = useRef({});
  const [showVariance, setShowVariance] = useState(true);
  const [ciCustomOpen, setCiCustomOpen] = useState(false);

  // Use extraGoals total when goals are set up — same source-of-truth as useDashboardData.
  // Fall back to legacy savLog snapshot for users who only use the Check-In balance log.
  const hasGoals = extraGoals.length > 0;
  const extraGoalsTotal = extraGoals.reduce((s, g) => s + (g.bal || 0), 0);
  const lat = hasGoals ? extraGoalsTotal : (savLog.length ? savLog[0].bal : 0);
  const totalGoalTarget = extraGoals.filter((g) => g.target > 0).reduce((s, g) => s + g.target, 0);
  const sg = hasGoals && totalGoalTarget > 0 ? totalGoalTarget : savGoal || 1000;
  const sp = Math.min(100, Math.round((lat / sg) * 100));
  const mos = savContrib > 0 && lat < sg ? Math.ceil((sg - lat) / savContrib) : '?';

  const today = new Date();
  const rv = calcPayoff(cards, extra, method, lump);
  const pr = rv.sched[0];

  const logSav = () => {
    const val = parseFloat(savBalRef.current?.value);
    if (isNaN(val) || val < 0) {
      showToast('Enter a valid balance');
      return;
    }
    if (val >= sg && lat < sg) {
      triggerConfetti?.();
    }
    const mo = MOS[today.getMonth()] + " '" + String(today.getFullYear()).slice(2);
    updateState({ savLog: [{ bal: val, mo, ts: Date.now() }, ...savLog] });
    if (savBalRef.current) savBalRef.current.value = '';
    showToast(val >= sg ? '🎉 Savings goal reached!' : 'Saved!');
  };

  const logCI = () => {
    const entry = {
      ts: Date.now(),
      mo: MOS[today.getMonth()] + " '" + String(today.getFullYear()).slice(2),
      actual: {},
      proj: {},
    };
    let any = false;
    const updatedCards = cards.map((c) => {
      const el = ciRefs.current[c.id];
      const v = parseFloat(el?.value);
      const p = pr ? pr[c.id] : c.bal;
      entry.proj[c.id] = p;
      entry.actual[c.id] = isNaN(v) ? p : v;
      if (!isNaN(v)) any = true;
      // Update actual card balance
      return isNaN(v) ? c : { ...c, bal: v };
    });
    if (!any) {
      showToast('Enter at least one balance');
      return;
    }
    cards.forEach((c) => {
      const el = ciRefs.current[c.id];
      if (el) el.value = '';
    });
    updateState({ checkins: [entry, ...checkins], cards: updatedCards });
    showToast('Check-in saved! Balances updated.');
  };

  const updateGoal = (v) => {
    const g = parseFloat(v) || 1000;
    updateState({ savGoal: g });
  };

  return (
    <div>
      {/* Savings */}
      <div className="card cp" style={{ marginBottom: 12 }}>
        <div className="st">💵 Savings Balance</div>

        {hasGoals ? (
          /* Goals mode — balances are tracked in the Savings tab; show a summary here */
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--grn)' }}>{fc(lat)}</span>
              {totalGoalTarget > 0 && <span style={{ fontSize: 11, color: 'var(--mut)' }}>{sp}% of {f0(totalGoalTarget)} target</span>}
            </div>
            {totalGoalTarget > 0 && (
              <div className="pw" style={{ marginBottom: 6 }}>
                <div className="pb" style={{ background: 'var(--grn)', width: sp + '%' }} />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
              {extraGoals.map((g) => {
                const gpct = g.target > 0 ? Math.min(100, Math.round((g.bal / g.target) * 100)) : null;
                return (
                  <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span>{g.emoji || '🎯'} {g.name}</span>
                    <span style={{ color: g.col || 'var(--grn)', fontWeight: 700 }}>
                      {fc(g.bal || 0)}{gpct !== null ? ` · ${gpct}%` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
            {savContrib > 0 && lat < sg && (
              <div style={{ fontSize: 11, color: 'var(--mut)' }}>
                ~{mos} months to goal at {fc(savContrib)}/mo · manage in 💵 Savings tab
              </div>
            )}
          </>
        ) : (
          /* Legacy single-goal mode */
          <>
            <div className="sgc">
              <input
                defaultValue={savGoalName}
                onBlur={(e) => { const v = e.target.value.trim(); if (v) updateState({ savGoalName: v }); }}
                style={{ fontSize: 12, fontWeight: 700, marginBottom: 3, background: 'none', border: 'none', borderBottom: '1px dashed var(--bdr)', color: 'var(--txt)', fontFamily: 'inherit', width: '100%', padding: '1px 0', cursor: 'text' }}
              />
              <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 7 }}>
                Goal: {f0(sg)} · Current: {fc(lat)}
              </div>
              <div className="pw">
                <div className="pb" style={{ background: 'var(--grn)', width: sp + '%' }} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 3 }}>
                {sp}% there · ~{mos} months at {fc(savContrib)}/mo
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div className="sgiw" style={{ marginBottom: 0, flex: 1 }}>
                <span>$</span>
                <input className="sgi" type="number" ref={savBalRef} placeholder="Current balance" />
              </div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                Goal $
                <input type="number" defaultValue={sg} style={{ width: 60, background: 'none', border: 'none', borderBottom: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 10, textAlign: 'right', padding: '1px 3px' }} onChange={(e) => updateGoal(e.target.value)} />
              </div>
            </div>
            <button className="btn" onClick={logSav}>Save Balance</button>
          </>
        )}
      </div>

      {/* Extra savings goals */}
      {extraGoals.length > 0 && (
        <div className="card cp" style={{ marginBottom: 12 }}>
          <div className="st">🎯 Savings Goals</div>
          {extraGoals.map((g) => {
            const isAccount = g.type === 'account';
            const pct =
              !isAccount && g.target > 0 ? Math.min(100, Math.round((g.bal / g.target) * 100)) : 0;
            return (
              <div key={g.id} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700 }}>
                    {g.emoji || ''} {g.name}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--mut)' }}>
                    {isAccount ? 'Account' : `${pct}% · goal ${f0(g.target)}`}
                  </span>
                </div>
                {!isAccount && g.target > 0 && (
                  <div className="pw" style={{ marginBottom: 4 }}>
                    <div
                      className="pb"
                      style={{ background: 'var(--grn)', width: pct + '%' }}
                    ></div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                  <div className="sgiw" style={{ marginBottom: 0, flex: 1 }}>
                    <span>$</span>
                    {/* key includes bal so the uncontrolled input resets when balance changes externally */}
                    <input
                      className="sgi"
                      type="number"
                      placeholder={g.bal || 0}
                      min="0"
                      key={g.id + ':' + g.bal}
                      defaultValue={g.bal || ''}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0)
                          updateState({
                            extraGoals: extraGoals.map((x) =>
                              x.id === g.id ? { ...x, bal: Math.round(v * 100) / 100 } : x
                            ),
                          });
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--grn)', fontWeight: 700 }}>
                    {fc(g.bal || 0)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Credit card balances */}
      <div className="card cp" style={{ marginBottom: 12 }}>
        <div className="st">📌 Update Balances</div>
        <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 12 }}>
          Enter your actual statement balances for{' '}
          {today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} — saves and
          updates everywhere
        </div>
        <div className="cig">
          {cards.map((c) => {
            const proj = pr ? pr[c.id] : c.bal;
            return (
              <div key={c.id} className="cif">
                <div className="cilb" style={{ color: c.col }}>
                  {c.name}
                </div>
                <div className="cip">
                  Projected: <strong>{f0(proj)}</strong>
                </div>
                <div className="ciw">
                  <span>$</span>
                  <input
                    className="cii"
                    type="number"
                    placeholder={Math.round(proj)}
                    min="0"
                    ref={(el) => (ciRefs.current[c.id] = el)}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <button className="btn" onClick={logCI}>
          Save &amp; Update Balances
        </button>
      </div>

      {/* History */}
      <div className="card cp">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <div className="st" style={{ marginBottom: 0 }}>
            📂 History
          </div>
          <button
            onClick={() => setCiCustomOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '4px 9px',
              background: ciCustomOpen ? 'var(--surf2)' : 'none',
              border: '1px solid var(--bdr)',
              borderRadius: 7,
              fontSize: 11,
              color: 'var(--mut)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            ⚙️ Customize {ciCustomOpen ? '▲' : '▼'}
          </button>
        </div>
        {ciCustomOpen && (
          <div
            style={{
              marginBottom: 10,
              padding: '10px 12px',
              background: 'var(--surf)',
              border: '1px solid var(--bdr)',
              borderRadius: 9,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--mut)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
                marginBottom: 8,
              }}
            >
              Table Options
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowVariance((v) => !v)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 99,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: '1px solid ' + (showVariance ? 'var(--grn)' : 'var(--bdr)'),
                  background: showVariance ? 'rgba(107,203,119,.12)' : 'none',
                  color: showVariance ? 'var(--grn)' : 'var(--mut)',
                }}
              >
                {showVariance ? '✓ ' : ''}Show Variance (+/−)
              </button>
            </div>
          </div>
        )}

        {savLog.length > 0 && (
          <>
            <div className="dv">Savings History</div>
            <div className="tw">
              <table className="ht2">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Month</th>
                    <th>Balance</th>
                    <th>Change</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {savLog.map((e, i) => {
                    const prev = savLog[i + 1];
                    const delta = prev ? e.bal - prev.bal : null;
                    return (
                      <tr key={i}>
                        <td>{e.mo}</td>
                        <td style={{ color: 'var(--grn)', fontWeight: 700 }}>{fc(e.bal)}</td>
                        <td>
                          {delta === null ? (
                            '—'
                          ) : (
                            <span className={delta >= 0 ? 'dp' : 'dn'}>
                              {delta >= 0 ? '+' : ''}
                              {f0(delta)}
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="del"
                            onClick={() =>
                              updateState({ savLog: savLog.filter((_, j) => j !== i) })
                            }
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {checkins.length > 0 && (
          <>
            <div className="dv" style={{ marginTop: 10 }}>
              Card Balance History
            </div>
            <div className="tw">
              <table className="ht2">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Month</th>
                    {cards.map((c) => (
                      <th key={c.id}>{c.name}</th>
                    ))}
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.map((ci, i) => {
                    const tA = cards.reduce((s, c) => s + (ci.actual[c.id] || 0), 0);
                    const tP = cards.reduce((s, c) => s + (ci.proj[c.id] || 0), 0);
                    const tD = tA - tP;
                    return (
                      <tr key={i}>
                        <td>{ci.mo}</td>
                        {cards.map((c) => {
                          const d = ci.actual[c.id] - ci.proj[c.id];
                          const cls = d < -10 ? 'dp' : d > 10 ? 'dn' : '';
                          return (
                            <td key={c.id}>
                              {f0(ci.actual[c.id])}
                              {showVariance && (
                                <span className={cls} style={{ fontSize: 9 }}>
                                  {' '}
                                  {d > 0 ? '+' : ''}
                                  {f0(d)}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className={showVariance ? (tD < -10 ? 'dp' : tD > 10 ? 'dn' : '') : ''}>
                          <strong>{f0(tA)}</strong>
                        </td>
                        <td>
                          <button
                            className="del"
                            onClick={() =>
                              updateState({ checkins: checkins.filter((_, j) => j !== i) })
                            }
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {showVariance && (
              <p style={{ fontSize: 10, color: 'var(--mut)', marginTop: 7 }}>
                🟢 Green = below projection. 🔴 Red = above projection.
              </p>
            )}
          </>
        )}

        {!savLog.length && !checkins.length && <div className="em">No history yet</div>}
      </div>
    </div>
  );
}
